<template>
  <div class="table-pdf card p-3">
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Caricamento...</span>
      </div>
      <div class="loading-text">Caricamento in corso...</div>
    </div>
    <!-- Header con titolo e pulsante download -->
    <div class="card mb-3 shadow-sm">
      <div class="card-body d-flex align-items-center justify-content-between">

        <!-- Titolo + icona filtro -->
        <div class="d-flex align-items-center gap-2">
          <h5 class="mb-0">Download massivi PDF ({{ pagination.total || 0 }})</h5>

          <!-- Pulsante mostra/nascondi filtri -->
          <button class="btn btn-outline-secondary btn-sm p-1" style="border-color: #D3D3D3; font-size: 0.8rem;"
            @click="showFilters = !showFilters">
            <i class="bi bi-funnel-fill"></i>
          </button>
        </div>

        <!-- Pulsante scarica ZIP -->
        <button class="btn btn-primary btn-sm" :disabled="selectedFiles.length < 2" @click="downloadSelectedFiles"
          title="Scarica PDF selezionati in ZIP">
          <i class="bi bi-download me-1"></i> Scarica ZIP
        </button>
      </div>
    </div>

    <!-- Filtri veri e propri, visibili solo se showFilters = true -->
    <div v-if="showFilters" class="d-flex gap-2 mb-3 align-items-end">

      <div class="d-flex flex-column">
        <label class="form-label fw-bold mb-1">Stato</label>
        <select v-model="statusFilter" class="form-select form-select-sm">
          <option value="">Tutti gli stati</option>
          <option value="done">Pronto per il download</option>
          <option value="downloaded">Scaricato</option>
        </select>
      </div>

      <div class="d-flex flex-column">
        <label class="form-label fw-bold mb-1">Data da</label>
        <input type="date" v-model="dateFromFilter" class="form-control form-control-sm">
      </div>

      <div class="d-flex flex-column">
        <label class="form-label fw-bold mb-1">Data a</label>
        <input type="date" v-model="dateToFilter" class="form-control form-control-sm">
      </div>

      <!-- Pulsante di ricerca allineato -->
      <div class="d-flex align-items-end">
        <button class="btn btn-primary btn-sm ms-1" style="height: 38px;" @click="loadFiles" title="Cerca">
          <i class="bi bi-search"></i>
        </button>
      </div>

    </div>
    <TableViewBo :columns="columns" :items="files" :pagination="pagination" :sort-field="sortField"
      :sort-direction="sortDirection" :show-search="false" :has-actions="false" @page-change="onPageChange"
      @sort="onSort" :all-selected="allSelected" @select-all="toggleSelectAll">

      <template #selected="{ item }">
        <div style="text-align: center;">
          <input type="checkbox" :value="item.id" v-model="selectedFiles" @change="onSelectionChange" />
        </div>
      </template>

      <template #file_name="{ item }">
        <div style="text-align: left;">{{ item.file_name }}</div>
      </template>

      <template #created_at="{ item }">
        <div style="text-align: center;">{{ formatDate(item.created_at) }}</div>
      </template>

      <template #updated_at="{ item }">
        <div style="text-align: center;">{{ formatDate(item.updated_at) }}</div>
      </template>

      <template #status="{ item }">
        <div :class="['state-badge', getStateClass(item.status)]" style="margin: 0 auto;">
          {{ getStateLabel(item.status) }}
        </div>
      </template>

      <template #actions="{ item }">
        <div style="display: flex; justify-content: center;">
          <button v-if="item.status === 'done' || item.status === 'downloaded'"
            class="btn btn-sm btn-primary text-white d-flex align-items-center justify-content-center"
            :disabled="downloadingIds.includes(item.id)" @click="downloadFile(item)" title="Scarica PDF"
            style="min-width: 40px;">
            <span v-if="downloadingIds.includes(item.id)">
              <span class="spinner-border spinner-border-sm"></span>
            </span>
            <i v-else class="bi bi-download"></i>
          </button>
        </div>
      </template>

      <template #empty>
        Nessun file disponibile.
      </template>

    </TableViewBo>
  </div>
</template>

<script>
import TableViewBo from '../common/TableViewBo.vue';

export default {
  name: 'ElencoDownloadView',
  components: { TableViewBo },
  data() {
    return {
      files: [],
      selectedFiles: [],
      pagination: { current_page: 1, per_page: 10, total: 0 },
      sortField: 'created_at',
      sortDirection: 'desc',
      columns: [
        { key: 'selected', label: '', width: '5%' },
        { key: 'file_name', label: 'Nome del File', width: '50%' },
        { key: 'created_at', label: 'Data Creazione', width: '10%', sortable: true },
        { key: 'updated_at', label: 'Data Aggiornamento', width: '10%', sortable: true },
        { key: 'status', label: 'Stato', width: '15%', sortable: true },
        { key: 'actions', label: '', width: '10%' }
      ],
      statusFilter: '', // '' | 'processing' | 'done' | 'downloaded'
      dateFromFilter: null,
      dateToFilter: null,
      showFilters: false,
      allSelected: false,
      isLoading: true,
      downloadingIds: [],
    }
  },
  mounted() {
    this.loadFiles();
  },
  watch: {
    selectedFiles(newVal) {
      this.allSelected =
        newVal.length === this.files.length && this.files.length > 0;
    },
    files() {
      this.selectedFiles = [];
      this.allSelected = false;
    }
  },
  methods: {
    onSelectionChange() {
      this.allSelected =
        this.selectedFiles.length === this.files.length &&
        this.files.length > 0;
    },
    toggleSelectAll(checked) {
      this.allSelected = checked;
      this.selectedFiles = checked ? this.files.map(f => f.id) : [];
    },
    async downloadFile(item) {
      this.downloadingIds.push(item.id);
      try {
        const response = await this.$root.$api.get(
          `/user/pdf-download/${item.id}`,
          { responseType: 'blob' }
        );

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', item.file_name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        const updateResponse = await this.$root.$api.post('/user/pdf-mark-downloaded', {
          ids: [item.id]
        });

        const updatedFile = updateResponse.data.files.find(f => f.id === item.id);
        if (updatedFile) {
          item.status = updatedFile.status;
        }

      } catch (err) {
        console.error('Errore download PDF:', err);
      } finally {
        this.downloadingIds = this.downloadingIds.filter(id => id !== item.id);
      }
    },
    async downloadSelectedFiles() {
      if (this.selectedFiles.length === 0) return;

      try {
        const response = await this.$root.$api.post(
          '/user/pdf-download-multiple',
          { ids: this.selectedFiles },
          { responseType: 'blob' }
        );

        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Quesiti_${new Date().toISOString().replace(/[:.]/g, '_')}.zip`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.files.forEach(f => {
          if (this.selectedFiles.includes(f.id) && f.status === 'done') {
            f.status = 'downloaded';
          }
        });

        this.selectedFiles = [];
      } catch (err) {
        console.error('Errore download multiplo:', err);
      }
    },
    getStateLabel(status) {
      switch (status) {
        case 'processing': return 'In elaborazione';
        case 'done': return 'Pronto per il download';
        case 'downloaded': return 'Scaricato';
        default: return status;
      }
    },
    async loadFiles() {
      this.isLoading = true;
      try {
        const params = {
          page: this.pagination.current_page,
          per_page: this.pagination.per_page,
          sortField: this.sortField,
          sortDirection: this.sortDirection,
          status: this.statusFilter || undefined,
          dateFrom: this.dateFromFilter || undefined,
          dateTo: this.dateToFilter || undefined,
        };

        const res = await this.$root.$api.get('/user/pdf-list', { params });

        this.files = res.data.data;
        this.pagination = {
          currentPage: res.data.current_page,
          perPage: res.data.per_page,
          total: res.data.total,
          lastPage: res.data.last_page,
          from: (res.data.current_page - 1) * res.data.per_page + 1,
          to: Math.min(res.data.current_page * res.data.per_page, res.data.total)
        };

      } catch (err) {
        console.error('Errore caricamento PDF:', err);
      } finally {
        this.isLoading = false;
      }
    }
    ,
    onPageChange(page) {
      this.pagination.current_page = page;
      this.loadFiles();
    },
    onSort({ field, direction }) {
      this.sortField = field;
      this.sortDirection = direction;
      this.loadFiles();
    },
    formatDate(d) {
      const date = new Date(d);
      return date.toLocaleDateString('it-IT') + ' ' + date.toLocaleTimeString('it-IT');
    },
    getStateClass(status) {
      switch (status) {
        case 'processing': return 'state-processing';
        case 'done': return 'state-done';
        case 'downloaded': return 'state-downloaded';
        default: return '';
      }
    }
  }
}
</script>

<style scoped>
.state-badge {
  padding: 0.2rem 0.5rem;
  border-radius: 1.25rem;
  display: inline-block;
  font-weight: bold;
  font-size: 0.85rem;
  text-align: center;
}

.table-pdf ::v-deep td .state-badge {
  display: flex;
  justify-content: center;
  align-items: center;
}

.state-processing {
  background-color: orange;
  color: white;
}

.state-done {
  background-color: green;
  color: white;
}

.state-downloaded {
  background-color: blue;
  color: white;
}

button.btn-outline-primary:hover {
  background-color: #0d6efd;
  color: #fff;
}

button.btn-primary:hover {
  background-color: #0b5ed7;
}

.table-pdf ::v-deep table thead th {
  text-align: center;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}
</style>
